/**
 * Calculates the periodic payment for a loan using the amortization formula,
 * and returns extra info for a partial loan period.
 * Returns RAW (unrounded) numeric values; round only at display.
 */
export function calculatePayment(
  principal,
  annualRate,
  years,
  loanLength,
  frequency = "monthly",
  interestOnly = false
) {
  // Treat falsy principal or years as invalid. Allow annualRate to be 0 (zero‑interest loan)
  if (!principal || !years) {
    return {
      payment: 0,
      total: 0,
      totalPaid: 0,
      remaining: 0,
      interestPaid: 0,
      principalPaid: 0,
      schedule: [],
    };
  }

  // Normalize numeric inputs
  principal = Number(principal);
  annualRate = Number(annualRate) || 0; // if undefined / NaN treat as 0
  years = Number(years);
  loanLength = Math.min(Number(loanLength), years);

  // Frequency map
  const FREQ = {
    monthly: { periodsPerYear: 12 },
    fortnightly: { periodsPerYear: 26 },
  };
  const freqData = FREQ[frequency];
  if (!freqData) throw new Error("Unsupported frequency");

  const periods = years * freqData.periodsPerYear;
  const loanPeriods = loanLength * freqData.periodsPerYear;
  const ratePerPeriod = annualRate / 100 / freqData.periodsPerYear;

  // Interest-only branch (UNROUNDED internals)
  if (interestOnly) {
    const payment = principal * ratePerPeriod; // may be 0 if ratePerPeriod 0
    const total = payment * periods;
    const totalPaid = payment * loanPeriods;
    const interestPaid = totalPaid; // all interest
    const principalPaid = 0;
    const remaining = principal; // unchanged
    const schedule = [];
    for (let i = 0; i < loanPeriods; i++) {
      schedule.push({
        period: i + 1,
        payment,
        interest: payment,
        principal: 0,
        remaining,
      });
    }
    return {
      payment,
      total,
      totalPaid,
      remaining,
      interestPaid,
      principalPaid,
      schedule,
    };
  }

  // Amortization (UNROUNDED)
  let payment;
  if (ratePerPeriod === 0) {
    payment = principal / periods;
  } else {
    payment =
      (principal * ratePerPeriod) / (1 - Math.pow(1 + ratePerPeriod, -periods));
  }

  const total = payment * periods;
  const totalPaid = payment * loanPeriods;

  // Iterative schedule becomes source of truth for remaining (no analytic remaining calc to avoid rounding mismatch)
  let balance = principal;
  let interestPaid = 0;
  let principalPaid = 0;
  const schedule = [];
  for (let i = 0; i < loanPeriods; i++) {
    const interest = ratePerPeriod === 0 ? 0 : balance * ratePerPeriod;
    let principalComponent = payment - interest;
    if (principalComponent > balance) principalComponent = balance; // cap overshoot
    interestPaid += interest;
    principalPaid += principalComponent;
    balance -= principalComponent;
    if (balance < 1e-9) balance = 0; // collapse floating dust
    schedule.push({
      period: i + 1,
      payment,
      interest,
      principal: principalComponent,
      remaining: balance,
    });
  }
  const remaining = balance; // raw

  return {
    payment,
    total,
    totalPaid,
    remaining,
    interestPaid,
    principalPaid,
    schedule,
  };
}
